import axios from "axios";
import { ActionTree } from "vuex";
import { GlobalInterface } from "../index.js";
import { StateInterface } from "./state";
import Playlist from "@/classes/Playlist";
import Schedule from "@/classes/Schedule";
import sharedMethods from "@/sharedMethods.js";

const actions: ActionTree<StateInterface, GlobalInterface> = {
	/**
	 * fetch schedules of a playlist
	 * @param playlistId id of the playlist with the schedules to fetch
	 * @returns response
	 */
	async fetchPlaylistSchedules({ rootState, commit }, playlistId) {
		const response = await axios.post(rootState.api + "getPlaylistSchedule/", {
			plID: playlistId,
		});

		const schedules: Schedule[] = [];

		response.data.forEach((schedule: any) => {
			schedules.push(Schedule.fromObject(schedule));
		});

		commit("setPlaylistSchedules", schedules);

		return schedules;
	},
	/**
	 * fetches all playlists of a user (contains a playlist object and content ids without schedule)
	 * @returns playlist array
	 * @see https://api-dev.mirrads.de/api-docs/#/Playlistmanagement/post_api_playlistmanagement_v2_get_playlists
	 */
	async fetchAllUserPlaylists(
		{ commit, rootState, dispatch },
		getSchedule: boolean
	) {
		return new Promise((resolve, reject) => {
			const info = {
				accId: rootState.user.AccID,
			};

			const promiseList = [];

			axios
				.post(rootState.api + "playlistmanagement/v2/get-playlists/", info)
				.then((response) => {
					const playlists: Playlist[] = [];
					response.data.forEach(
						(playlist: {
							ID: number;
							ACCID: number;
							NAME: string;
							COLOR: string;
							contentIds: string[];
							deviceIds: number[];
							groupIds: number[];
						}) => {
							playlists.push(Playlist.fromObject(playlist));

							if (getSchedule) {
								promiseList.push(
									dispatch("fetchPlaylistSchedules", playlist.ID)
								);
							}
						}
					);

					commit("setAllUserPlaylists", playlists);

					Promise.all(promiseList).then(() => {
						resolve(playlists);
					});
				});
		});
	},
	/**
	 * replaces device assigned playlists with the given ones (list of ids)
	 * @param { deviceId: number, playlistIds: Array<number> } payload PlaylistIDs + DeviceID for Playlist updates
	 */
	async updateDevicePlaylists(
		{ dispatch, commit, rootState },
		{ deviceId, playlistIds }
	) {
		const payload = {
			AccID: rootState.user.AccID,
			PLID: playlistIds,
			DID: deviceId,
		};

		commit("updateDevicePlaylists", { deviceId, playlistIds });

		return axios
			.post(rootState.api + "set-device-playlists/", payload)
			.catch((err) => {
				console.log(err);
				dispatch("fetchAllUserPlaylists");
			});
	},
	/**
	 * replaces device assigned playlists with the given ones (list of ids)
	 * @param { deviceId: number, playlistIds: Array<number> } payload PlaylistIDs + DeviceID for Playlist updates
	 */
	async updateGroupPlaylists(
		{ dispatch, commit, rootState },
		{ groupId, playlistIds }
	) {
		const payload = {
			accId: rootState.user.AccID,
			playlistIds: playlistIds,
			groupId: groupId,
		};

		commit("updateGroupPlaylists", { groupId, playlistIds });

		return axios
			.post(rootState.api + "set-group-playlists/", payload)
			.catch((err) => {
				console.log(err);
				dispatch("fetchAllUserPlaylists");
			});
	},

	/**
	 * removes a playlist from the given device
	 * @param payload playlist id + device id
	 * @returns response
	 */
	async removePlaylistFromDevice({ rootState }, payload) {
		console.log("remove PL" + payload.playlist + " from " + payload.device);

		const info = {
			AccID: rootState.user.AccID,
			PLID: payload.playlist,
			DID: payload.device,
		};

		// todo commit
		const response = await axios.post(
			rootState.api + "RemoveDevicePlaylist/",
			info
		);

		return response;
	},

	/**
	 * deletes a playlist by its id
	 * @param playlistID id of the playlist to delete
	 * @see https://api-dev.mirrads.de/api-docs/#/Playlistmanagement/post_api_deletePlaylist
	 * @return response
	 */
	async deletePlaylistByID({ commit, dispatch, rootState }, playlistID) {
		const info = {
			item: playlistID,
		};

		const response = await axios.post(rootState.api + "deletePlaylist/", info);

		if (response.status === 200) {
			commit("removePlaylist", playlistID);
		}
		return response;
	},

	/**
	 * edit playlist settings (name, color)
	 * @param payload  plID + plName + plColor
	 * @returns response
	 */
	async editPlaylistByID({ dispatch, rootState, commit }, payload) {
		payload.accId = rootState.user.AccID;

		return new Promise((resolve, reject) => {
			axios.post(rootState.api + "editPlaylist", payload).then((response) => {
				commit("editPlaylistByID", payload);
				//dispatch("fetchAllUserPlaylists");
				resolve(response);
			});
		});
	},

	/**
	 * checks if a content id is in a playlist
	 * // do we really need a endpoint here?
	 * @param contentIds
	 */
	async checkIfContentIsInAPlaylist({ rootState }, contentIds) {
		return new Promise((resolve, reject) => {
			const info = {
				contentIds: contentIds,
			};

			axios.post(rootState.api + "checkUsedMedia/", info).then((response) => {
				resolve(response);
			});
		});
	},

	/**
	 * check if any device is using this playlist
	 * @param playlistID playlist id
	 * @returns response
	 */
	async checkIfPlaylistIsInUseByID({ rootState }, playlistID) {
		return new Promise((resolve, reject) => {
			const info = {
				item: playlistID,
			};

			axios.post(rootState.api + "checkUsedPlaylist", info).then((response) => {
				resolve(response);
			});
		});
	},

	/**
	 * updates the content of playlist
	 * @param payload PlaylistID + contentIds for Playlist updates
	 * @return promise
	 * @see https://api-dev.mirrads.de/api-docs/#/Playlistmanagement/post_api_playlistmanagement_v2_update_playlist
	 */
	async updatePlaylistContentV2({ commit, rootState }, payload) {
		return new Promise((resolve, reject) => {
			const info = {
				AccID: rootState.user.AccID,
				playlistId: payload.playlistId,
				contentIds: payload.content,
			};

			commit("updatePlaylist", info);

			axios
				.post(rootState.api + "playlistmanagement/v2/update-playlist/", info)
				.then((response) => {
					console.log(response);
					resolve(response);
				});
		});
	},

	/**
	 * creates a new playlist
	 * @param data plName + plColor
	 * @returns response
	 * @see https://api-dev.mirrads.de/api-docs/#/Playlistmanagement/post_api_createPlaylist
	 */
	async createPlaylist({ rootState }, data) {
		data.accID = rootState.user.AccID;
		return new Promise((resolve, reject) => {
			axios.post(rootState.api + "createPlaylist", data).then((response) => {
				resolve(response);
			});
		});
		// todo commit
	},

	/**
	 * updates a schedule for the given playlist and schedule id
	 * @param data use Schedule.toObject() method
	 * @returns response
	 */
	async updatePlaylistSchedule({ rootState, commit }, data) {
		return new Promise((resolve, reject) => {
			axios
				.post(
					rootState.api + "playlistmanagement/v2/update-playlist-schedule/",
					data
				)
				.then((response) => {
					const schedules: Schedule[] = [];
					response.data.forEach((schedule: any) => {
						schedules.push(Schedule.fromObject(schedule));
					});

					commit("setPlaylistSchedules", schedules);
					resolve(response);
				});
		});
	},

	/**
	 * deletes a schedule
	 * @param data schedID + plID
	 * @returns response
	 */
	async deletePlaylistSchedule({ rootState, commit }, data) {
		return new Promise((resolve, reject) => {
			axios
				.post(rootState.api + "deletePlaylistSchedule/", data)
				.then((response) => {
					if (response.data != "Every playlist needs atleast one schedule") {
						// response data contains new schedules at index 1 and deleted schedule at index 0
						const schedules: Schedule[] = [];
						response.data[1].forEach((schedule: any) => {
							schedules.push(Schedule.fromObject(schedule));
						});

						commit("setPlaylistSchedules", schedules);
					} else {
						sharedMethods.warnNotify(response.data);
					}
					resolve(response);
				});
		});
	},
};
export default actions;
